/**
 * UnansweredQuestions — public card stack of press questions that official
 * WHO/CDC communications have not answered.
 *
 * Each card shows:
 *   - The question text
 *   - A status badge (NO_ANSWER red, PARTIAL amber, ANSWERED green)
 *   - "Asked by N outlets" pill
 *   - A disclosure listing the source URLs (expand/collapse)
 *
 * Design rules (uncodixify-ui.md):
 *   - No hover lift, no shadow escalation, no animate-pulse on badges.
 *   - transition-colors duration-150 only.
 *   - rounded-lg for cards, rounded-md for buttons.
 *
 * RTL rules (rtl-support-arabic.md):
 *   - Logical CSS only: ms-*, me-*, text-start.
 *   - No rtl:flex-row-reverse.
 *   - ChevronDown mirrors if needed (vertical — no mirroring required).
 *
 * Touch targets: expand button has min-h-[44px] via parent wrapper.
 *
 * Prompt #09 (timeline-contradictions-and-unanswered-questions).
 */

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { TranslateFn } from "#/lib/i18n.js";
import { useUnansweredQuestions } from "#/hooks/useUnansweredQuestions.js";
import type { UnansweredQuestionItem } from "#/hooks/useUnansweredQuestions.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UnansweredQuestionsProps {
  locale: string;
  t: TranslateFn;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({
  status,
  t,
}: {
  status: "NO_ANSWER" | "PARTIAL" | "ANSWERED";
  t: TranslateFn;
}) {
  const label =
    status === "NO_ANSWER"
      ? t("contradictions:questions.status.noAnswer")
      : status === "PARTIAL"
        ? t("contradictions:questions.status.partial")
        : t("contradictions:questions.status.answered");

  const colourClass =
    status === "NO_ANSWER"
      ? "bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800"
      : status === "PARTIAL"
        ? "bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
        : "bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${colourClass}`}
      role="status"
      aria-label={label}
    >
      {label}
    </span>
  );
}

function QuestionCard({
  item,
  t,
}: {
  item: UnansweredQuestionItem;
  t: TranslateFn;
}) {
  const [expanded, setExpanded] = useState(false);

  const outletCount = item.sourceUrls.length;
  const expandLabel = expanded
    ? t("contradictions:questions.hideSources")
    : t("contradictions:questions.viewSources");

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="px-4 py-4">
        {/* Header row: status + outlet pill */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <StatusBadge status={item.status} t={t} />
          <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground" /* uncodixify-ok: outlet count pill tag */>
            {t("contradictions:questions.askedBy", { count: outletCount })}
          </span>
        </div>

        {/* Question text */}
        <p className="text-sm font-medium text-foreground text-start">
          {item.question}
        </p>

        {/* Expand / collapse sources */}
        {outletCount > 0 && (
          <div className="mt-3">
            <div className="flex min-h-[44px] items-center">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className="inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline transition-colors duration-150"
              >
                {expandLabel}
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-150 ${
                    expanded ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {expanded && (
              <ul
                className="mt-1 space-y-1 ps-4 border-s-2 border-border"
                aria-label={t("contradictions:questions.viewSources")}
              >
                {item.sourceUrls.map((url, idx) => (
                  <li key={url}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary underline-offset-2 hover:underline transition-colors duration-150 line-clamp-1"
                      aria-label={t("contradictions:questions.sourceLink", {
                        n: idx + 1,
                      })}
                    >
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function UnansweredQuestions({ locale: _locale, t }: UnansweredQuestionsProps) {
  const { data, isLoading, isError } = useUnansweredQuestions();

  return (
    <section
      id="unanswered-questions"
      aria-labelledby="unanswered-questions-heading"
      className="space-y-4"
    >
      <div>
        <h2
          id="unanswered-questions-heading"
          className="text-xl font-semibold text-foreground"
        >
          {t("contradictions:questions.heading")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("contradictions:questions.subheading")}
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("contradictions:questions.loading")}
        </p>
      )}

      {isError && !isLoading && (
        <p
          className="text-sm text-destructive"
          role="alert"
          aria-live="assertive"
        >
          {t("contradictions:questions.error")}
        </p>
      )}

      {!isLoading && !isError && data?.length === 0 && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("contradictions:questions.empty")}
        </p>
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <div className="grid gap-3 @md:grid-cols-2">
          {data.map((item) => (
            <QuestionCard key={item.id} item={item} t={t} />
          ))}
        </div>
      )}
    </section>
  );
}
